import { useState } from 'react';
import {
  Button,
  Card,
  Checkbox,
  EffectiveValue,
  ErrorState,
  FormField,
  LoadingState,
  Modal,
  PanelTitle,
  ReasonField,
  Select,
  Stat,
  StatusPill,
  TextField,
  isReasonValid,
} from '../../components';
import { formatDateTime } from '../../lib/format/dates';
import { notify } from '../../services/notify';
import type { EffectiveLever, LeverValue } from './leversApi';
import { useAuditLog, useLocaleLevers, usePatchLocaleLevers } from './leversQueries';
import { GROUP_LABEL, LEVER_LABEL, formatLeverValue, leverLabel } from './parts';

/**
 * A locale's levers in force (B2): its payments / payouts / status state, then
 * every lever with its effective value and source, grouped. The page owns the
 * Edit levers button (Operators and up) and opens the editor through
 * `editing`; every change lands in the audit trail below.
 */
export function LeversPanel({
  localeId,
  editing,
  onEditingChange,
}: {
  localeId: string;
  editing: boolean;
  onEditingChange: (editing: boolean) => void;
}) {
  const q = useLocaleLevers(localeId);

  if (q.isPending)
    return (
      <Card surface="panel">
        <LoadingState label="Loading levers…" />
      </Card>
    );
  if (q.isError)
    return (
      <Card surface="panel">
        <ErrorState error={q.error} onRetry={() => void q.refetch()} compact />
      </Card>
    );

  const d = q.data;
  const groups = ['money', 'blue_star', 'norms'] as const;

  return (
    <Card surface="panel">
      <PanelTitle eyebrow="Levers in force">{d.localeName} configuration</PanelTitle>

      <div className="mb-4 grid grid-cols-3 gap-2.5">
        <Stat label="Payments" value={d.state.paymentsEnabled ? 'On' : 'Off'} hint="max price above $0" />
        <Stat label="Chef payouts" value={d.state.payoutsEnabled ? 'On' : 'Off'} hint="off by default" />
        <Stat label="Status" value={d.state.status === 'live' ? 'Live' : 'Inactive'} hint="set by locale type" />
      </div>

      {groups.map((g) => {
        const levers = d.levers.filter((l) => l.group === g);
        if (!levers.length) return null;
        return (
          <div key={g} className="mb-4">
            <div className="mb-2 text-[11px] font-bold uppercase tracking-[.14em] text-muted">{GROUP_LABEL[g]}</div>
            <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-2.5">
              {levers.map((l) => (
                <div key={l.key} className="min-w-0 rounded-[10px] bg-cream-warm px-[13px] py-[11px]">
                  <div className="text-[10px] font-bold uppercase tracking-[.14em] text-muted">{leverLabel(l.key)}</div>
                  <div className="mt-1 text-sm">
                    <EffectiveValue value={formatLeverValue(l, l.value)} source={l.source} />
                  </div>
                  {LEVER_LABEL[l.key]?.hint && (
                    <div className="mt-0.5 text-xs text-muted">{LEVER_LABEL[l.key]!.hint}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}

      <AuditTrail entityType="locale_settings" entityId={localeId} />

      {editing && (
        <EditLevers localeId={localeId} name={d.localeName} levers={d.levers} onClose={() => onEditingChange(false)} />
      )}
    </Card>
  );
}

/** Each lever's draft: `inherit` = no override (the default applies). */
type Draft = Record<string, { inherit: boolean; raw: string }>;

function toRaw(l: EffectiveLever, v: LeverValue): string {
  if (v === null || v === undefined) return '';
  if (l.kind === 'decimal') return String(Math.round(Number(v) * 1e6) / 1e4); // shown as percent
  if (l.kind === 'cents') return (Number(v) / 100).toFixed(2); // shown as dollars
  return String(v);
}

function fromRaw(l: EffectiveLever, raw: string): LeverValue {
  if (l.kind === 'bool') return raw === 'true';
  if (l.kind === 'string' || l.kind === 'enum') return raw.trim();
  const n = Number(raw);
  if (l.kind === 'decimal') return Math.round((n / 100) * 1e6) / 1e6;
  if (l.kind === 'cents') return Math.round(n * 100);
  return n;
}

function EditLevers({
  localeId,
  name,
  levers,
  onClose,
}: {
  localeId: string;
  name: string;
  levers: EffectiveLever[];
  onClose: () => void;
}) {
  const save = usePatchLocaleLevers(localeId);
  const [draft, setDraft] = useState<Draft>(() =>
    Object.fromEntries(
      levers.map((l) => [l.key, { inherit: l.override === null, raw: toRaw(l, l.override ?? l.value) }]),
    ),
  );
  const [reason, setReason] = useState('');

  // Only what changed goes to the BE; null = back to the default.
  const changes: Record<string, LeverValue> = {};
  for (const l of levers) {
    const d = draft[l.key]!;
    const next = d.inherit ? null : fromRaw(l, d.raw);
    if (next !== l.override) changes[l.key] = next;
  }
  const changed = Object.keys(changes);
  const needsReason = levers.some((l) => changed.includes(l.key) && l.requiresReason);
  const invalid = changed.some((k) => {
    const v = changes[k];
    return typeof v === 'number' && !Number.isFinite(v);
  });
  const blocked = !changed.length || invalid || (needsReason && !isReasonValid(reason));

  const set = (key: string, patch: Partial<Draft[string]>) =>
    setDraft((d) => ({ ...d, [key]: { ...d[key]!, ...patch } }));

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="xl"
      title={`Edit ${name} levers`}
      footer={
        <>
          <Button variant="ghost-dark" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={blocked}
            loading={save.isPending}
            onClick={() =>
              save.mutate(
                { changes, reason: reason.trim() },
                {
                  onSuccess: (res) => {
                    notify.success(`${res.changed ?? changed.length} lever${changed.length === 1 ? '' : 's'} updated.`);
                    onClose();
                  },
                },
              )
            }
          >
            Save {changed.length ? `(${changed.length})` : ''}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-x-5 gap-y-3.5">
        {levers.map((l) => {
          const d = draft[l.key]!;
          const unit = l.kind === 'decimal' ? '%' : l.kind === 'cents' ? '$' : undefined;
          return (
            <FormField
              key={l.key}
              label={
                <span className="inline-flex items-center gap-2">
                  {leverLabel(l.key)}
                  {changed.includes(l.key) && <StatusPill tone="info">changed</StatusPill>}
                </span>
              }
              hint={d.inherit ? `Default: ${formatLeverValue(l, l.value)}` : LEVER_LABEL[l.key]?.hint}
            >
              <div className="flex items-center gap-2.5">
                <div className="min-w-0 flex-1">
                  {l.kind === 'bool' ? (
                    <Select
                      surface="white"
                      compact
                      disabled={d.inherit}
                      aria-label={leverLabel(l.key)}
                      value={d.raw}
                      options={[
                        { value: 'true', label: 'On' },
                        { value: 'false', label: 'Off' },
                      ]}
                      onChange={(e) => set(l.key, { raw: e.target.value })}
                    />
                  ) : l.kind === 'enum' ? (
                    <Select
                      surface="white"
                      compact
                      disabled={d.inherit}
                      aria-label={leverLabel(l.key)}
                      value={d.raw}
                      options={l.enum ?? []}
                      onChange={(e) => set(l.key, { raw: e.target.value })}
                    />
                  ) : (
                    <TextField
                      surface="white"
                      aria-label={leverLabel(l.key)}
                      disabled={d.inherit}
                      inputMode={l.kind === 'string' ? 'text' : 'decimal'}
                      value={d.raw}
                      trailing={unit}
                      onChange={(e) => set(l.key, { raw: e.target.value })}
                    />
                  )}
                </div>
                <Checkbox
                  checked={d.inherit}
                  onChange={(e) => set(l.key, { inherit: e.target.checked })}
                  label="Default"
                />
              </div>
            </FormField>
          );
        })}
      </div>

      <div className="mt-4">
        {needsReason ? (
          <ReasonField value={reason} onChange={setReason} />
        ) : (
          <ReasonField value={reason} onChange={setReason} label="Reason (optional)" hint="Norms don't require one." />
        )}
      </div>
      {save.error && <ErrorState error={save.error} compact />}
    </Modal>
  );
}

/** Recent changes to one entity's levers, newest first. */
export function AuditTrail({ entityType, entityId }: { entityType: string; entityId: string }) {
  const q = useAuditLog({ entityType, entityId, page: 1, pageSize: 10 });
  const rows = q.data?.items ?? [];
  return (
    <div>
      <div className="mb-2 text-[11px] font-bold uppercase tracking-[.14em] text-muted">Change history</div>
      {q.isPending ? (
        <div className="text-[13px] text-muted">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-[13px] text-muted">No changes recorded yet.</div>
      ) : (
        rows.map((r) => {
          const kind = { kind: guessKind(r.fieldName), key: r.fieldName } as EffectiveLever;
          return (
            <div key={r.id} className="border-t border-[rgba(143,68,32,.08)] py-2 text-[13px]">
              <div className="flex flex-wrap items-baseline gap-x-2">
                <span className="font-semibold text-ink">{leverLabel(r.fieldName)}</span>
                <span className="tabular-nums text-muted">
                  {formatLeverValue(kind, r.oldValue)} → {formatLeverValue(kind, r.newValue)}
                </span>
                <span className="ml-auto text-xs text-muted">
                  {r.changedByName || 'Someone'} · {formatDateTime(r.changedAt)}
                </span>
              </div>
              {r.reason && <div className="mt-0.5 text-muted">“{r.reason}”</div>}
            </div>
          );
        })
      )}
      {(q.data?.total ?? 0) > 10 && (
        <div className="pt-1.5 text-xs text-muted">Showing the latest 10 of {q.data!.total}.</div>
      )}
    </div>
  );
}

/** Audit rows carry no kind; the lever key tells it (…Cents, …Percentage, …Enabled). */
function guessKind(key: string): EffectiveLever['kind'] {
  if (/Cents$/.test(key)) return 'cents';
  if (/Percentage$|PercentFee$/.test(key)) return 'decimal';
  if (/Enabled$|Taxable$/.test(key)) return 'bool';
  return 'float';
}
